import { Booking, Customer, PaymentHistoryEntry } from '../types/crm';

export interface PaymentStatus {
  isPaid: boolean;
  lastPaymentDate?: string;
  unpaidBookings: Booking[];
  paidBookings: Booking[];
  totalUnpaidAmount: number;
  totalPaidAmount: number;
  unpaidAttendeeCount: number;
  paidAttendeeCount: number;
}

export interface BookingPaymentSummary {
  bookingId: string;
  courseName: string;
  courseDate: string;
  attendeeCount: number;
  totalPrice?: number;
  isPaid: boolean;
  paymentDate?: string;
}

/**
 * Calculate payment status based on bookings rather than certificates
 */
export class PaymentStatusService {
  private static bookingPayerId(booking: Booking): string | undefined {
    return booking.payerCustomerId || booking.customerId;
  }

  /**
   * Calculate payment status for a customer based on their bookings
   */
  static calculatePaymentStatus(customer: Customer, bookings: Booking[]): PaymentStatus {
    const customerBookings = bookings.filter((booking) => this.bookingPayerId(booking) === customer.id);
    
    const paidBookings = customerBookings.filter(booking => booking.status === 'job_paid');
    const unpaidBookings = customerBookings.filter(booking => 
      booking.status !== 'job_paid' && 
      booking.status !== 'cancelled' &&
      booking.status !== 'completed' // Completed bookings that aren't paid are still unpaid
    );

    const totalPaidAmount = paidBookings.reduce((sum, booking) => sum + (booking.totalPrice || 0), 0);
    const totalUnpaidAmount = unpaidBookings.reduce((sum, booking) => sum + (booking.totalPrice || 0), 0);
    
    const paidAttendeeCount = paidBookings.reduce((sum, booking) => sum + booking.attendeeCount, 0);
    const unpaidAttendeeCount = unpaidBookings.reduce((sum, booking) => sum + booking.attendeeCount, 0);

    // Get the most recent payment date from payment history
    const lastPaymentDate = customer.paymentHistory.length > 0 
      ? customer.paymentHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date
      : undefined;

    return {
      isPaid: unpaidBookings.length === 0 && customerBookings.length > 0, // Paid if no unpaid bookings and has bookings
      lastPaymentDate,
      unpaidBookings,
      paidBookings,
      totalUnpaidAmount,
      totalPaidAmount,
      unpaidAttendeeCount,
      paidAttendeeCount
    };
  }

  /**
   * Get a summary of all bookings with their payment status
   */
  static getBookingPaymentSummary(customer: Customer, bookings: Booking[]): BookingPaymentSummary[] {
    const customerBookings = bookings.filter((booking) => this.bookingPayerId(booking) === customer.id);
    
    return customerBookings.map(booking => {
      const isPaid = booking.status === 'job_paid';
      
      // Find payment date from payment history
      const paymentEntry = customer.paymentHistory.find(entry => 
        entry.courseDetails.some(course => course.bookingId === booking.id)
      );
      
      return {
        bookingId: booking.id,
        courseName: booking.courseName,
        courseDate: booking.courseDate,
        attendeeCount: booking.attendeeCount,
        totalPrice: booking.totalPrice,
        isPaid,
        paymentDate: paymentEntry?.date
      };
    });
  }

  /**
   * Get payment status display text
   */
  static getPaymentStatusText(paymentStatus: PaymentStatus): string {
    if (paymentStatus.unpaidBookings.length === 0 && paymentStatus.paidBookings.length === 0) {
      return 'No Bookings';
    }
    
    if (paymentStatus.isPaid) {
      return 'All Paid';
    }
    
    if (paymentStatus.paidBookings.length === 0) {
      return 'Unpaid';
    }
    
    return 'Partially Paid';
  }

  /**
   * Get payment status color class
   */
  static getPaymentStatusColor(paymentStatus: PaymentStatus): string {
    if (paymentStatus.unpaidBookings.length === 0 && paymentStatus.paidBookings.length === 0) {
      return 'bg-slate-100 text-slate-600';
    }
    
    if (paymentStatus.isPaid) {
      return 'bg-emerald-100 text-emerald-600';
    }
    
    if (paymentStatus.paidBookings.length === 0) {
      return 'bg-red-100 text-red-600';
    }
    
    return 'bg-amber-100 text-amber-600';
  }

  /**
   * Get payment status background color
   */
  static getPaymentStatusBackground(paymentStatus: PaymentStatus): string {
    if (paymentStatus.unpaidBookings.length === 0 && paymentStatus.paidBookings.length === 0) {
      return 'bg-slate-50 border-slate-200';
    }
    
    if (paymentStatus.isPaid) {
      return 'bg-emerald-50 border-emerald-200';
    }
    
    if (paymentStatus.paidBookings.length === 0) {
      return 'bg-red-50 border-red-200';
    }
    
    return 'bg-amber-50 border-amber-200';
  }
}
